import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface BulkUpdateModalProps {
  isOpen: boolean;
  isEnabling?: boolean;
  onClose: () => void;
  onConfirm: (applyToExisting: boolean) => void;
  title?: string;
  descriptionEnabling?: string;
  descriptionDisabling?: string;
  applyToAll?: string;
  futureOnly?: string;
  cancel?: string;
}

const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({
  isOpen,
  isEnabling = false,
  onClose,
  onConfirm,
  title,
  descriptionEnabling,
  descriptionDisabling,
  applyToAll,
  futureOnly,
  cancel,
}) => {
  const { t } = useTranslation('adminPolicy');
  if (!isOpen) return null;

  const titleText = title ?? t('bulkModal.title');
  const descriptionText = isEnabling
    ? (descriptionEnabling ?? t('bulkModal.descriptionEnabling'))
    : (descriptionDisabling ?? t('bulkModal.descriptionDisabling'));
  const applyToAllText = applyToAll ?? t('bulkModal.applyToAll');
  const futureOnlyText = futureOnly ?? t('bulkModal.futureOnly');
  const cancelText = cancel ?? t('bulkModal.cancel');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                <ExclamationTriangleIcon
                  className="h-6 w-6 text-yellow-600"
                  aria-hidden="true"
                />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {titleText}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {descriptionText}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => onConfirm(true)}
            >
              {applyToAllText}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => onConfirm(false)}
            >
              {futureOnlyText}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateModal;

